'use client'

import React, { useState } from 'react'
import type { ExpandingRowsBlock as ExpandingRowsBlockProps, Expandingrow } from '@/payload-types'
import { ToggleButton } from '@/components/ui/toggle-button'
import { cn } from '@/utilities/ui'
import { safeJsonLdStringify } from '@/components/structuredData'
import RichText from '@/components/RichText'
import { getBlockPadding } from '@/utilities/getBlockPadding'

// Type guard to check if an item is a populated Expandingrow object
function isPopulatedExpandingrow(item: unknown): item is Expandingrow {
  return (
    item !== null &&
    item !== undefined &&
    typeof item === 'object' &&
    'id' in item &&
    'title' in item &&
    'category' in item &&
    typeof (item as Expandingrow).id === 'string' &&
    typeof (item as Expandingrow).title === 'string' &&
    ((item as Expandingrow).category === 'faq' || (item as Expandingrow).category === 'commitment') &&
    // At least one content field must be present
    (Boolean((item as Expandingrow).content) || Boolean((item as Expandingrow).richContent))
  )
}

// Extract plain text from Lexical richContent for schema markup
function extractPlainText(richContent: Expandingrow['richContent']): string {
  if (!richContent || !richContent.root || !richContent.root.children) {
    return ''
  }

  const extractFromNode = (node: unknown): string => {
    if (!node || typeof node !== 'object') return ''

    const nodeObj = node as Record<string, unknown>

    // Text node
    if (nodeObj.type === 'text' && typeof nodeObj.text === 'string') {
      return nodeObj.text
    }

    // Node with children
    if (Array.isArray(nodeObj.children)) {
      return nodeObj.children.map(extractFromNode).join('')
    }

    return ''
  }

  return richContent.root.children
    .map(extractFromNode)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Check if richContent actually has content (not just empty structure)
// Uses early exit for performance - stops as soon as any text is found
function hasRichContent(richContent: Expandingrow['richContent']): boolean {
  if (!richContent?.root?.children?.length) return false

  const hasText = (node: unknown): boolean => {
    if (!node || typeof node !== 'object') return false
    const n = node as Record<string, unknown>
    if (n.type === 'text' && typeof n.text === 'string' && n.text.trim()) return true
    if (Array.isArray(n.children)) return n.children.some(hasText)
    return false
  }

  return richContent.root.children.some(hasText)
}

interface ExpandingItemProps {
  item: Expandingrow & { number: string }
  isExpanded: boolean
  onToggle: () => void
}

type Props = ExpandingRowsBlockProps & {
  disableInnerContainer?: boolean
}

function ExpandingItemComponent({ item, isExpanded, onToggle }: ExpandingItemProps) {
  const hasContent = hasRichContent(item.richContent) || item.content

  return (
    <div className="flex flex-col w-full">
      {/* Title Row */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={`expanding-content-${item.id}`}
        className={cn(
          'flex items-center justify-between gap-4 w-full cursor-pointer pt-6',
          isExpanded ? 'pb-0' : 'pb-6',
        )}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
      >
        {/* Mobile: Just title text, Desktop: Number + title */}
        <div className="flex items-center flex-1 min-w-0">
          {/* Number - only show on desktop */}
          <div className="hidden lg:block lg:mr-[193px] body-copy-medium font-medium text-black">
            {item.number}
          </div>

          {/* Title */}
          <div className="subheading-medium text-black">
            {item.title}
          </div>
        </div>

        <ToggleButton
          isExpanded={isExpanded}
          selectedColor="black"
          unselectedColor="lime"
        />
      </div>

      {/* Content with smooth CSS Grid animation */}
      {hasContent && (
        <div
          id={`expanding-content-${item.id}`}
          role="region"
          aria-hidden={!isExpanded}
          className="grid transition-[grid-template-rows] duration-300 ease-in-out"
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div
              className={cn(
                'pt-4 pb-6 transition-opacity duration-300 ease-in-out',
                isExpanded ? 'opacity-100' : 'opacity-0',
              )}
            >
              <div className="body-copy-medium text-black lg:max-w-[874px] lg:ml-[209px]">
                {hasRichContent(item.richContent) ? (
                  <RichText
                    data={item.richContent!}
                    enableProse={false}
                    enableGutter={false}
                    className="prose-none [&_strong]:text-black [&_b]:text-black [&_a]:underline [&_a]:underline-offset-2 [&_ul]:list-disc [&_ul]:ml-4 [&_li]:mb-1 [&_p]:text-black/70 [&_p]:mb-4 last:[&_p]:mb-0 [&_code]:bg-red-50 [&_code]:rounded-[4px] [&_code]:p-0.5 [&_code]:font-mono [&_code]:text-[0.875em] [&_code]:text-red-600 [&_code]:border [&_code]:border-red-200"
                  />
                ) : (
                  item.content?.split('\n\n').map((paragraph, pIndex, paragraphs) => (
                    <p key={pIndex} className={cn("text-black/70", pIndex === paragraphs.length - 1 ? "mb-0" : "mb-4")}>
                      {paragraph}
                    </p>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const ExpandingRowsBlock: React.FC<Props> = ({ items, disableInnerContainer, paddingTop, paddingBottom }) => {
  // Get padding classes based on layout options
  const paddingClasses = getBlockPadding({
    paddingTop: paddingTop ?? true,
    paddingBottom: paddingBottom ?? true,
  })
  // Process the data from CMS with proper type safety
  const itemsArray = Array.isArray(items) ? items : []
  const processedItems: (Expandingrow & { number: string })[] = itemsArray
    .filter((item): item is Expandingrow => isPopulatedExpandingrow(item))
    .map((item, index) => ({
      ...item,
      number: String(index + 1).padStart(2, '0'),
    }))

  const [expandedItem, setExpandedItem] = useState<string | null>(
    processedItems.length > 0 ? processedItems[0].id : null
  )

  const toggleItem = (id: string) => {
    setExpandedItem(prev => prev === id ? null : id)
  }

  // Build FAQ schema for SEO (only for FAQ-category items)
  const faqItems = processedItems.filter(item => item.category === 'faq')
  const faqSchema = faqItems.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: hasRichContent(item.richContent)
          ? extractPlainText(item.richContent)
          : item.content || '',
      },
    })),
  } : null

  if (processedItems.length === 0) {
    return (
      <div className={cn(
        "box-border flex flex-col gap-[42px] items-center justify-center relative size-full",
        paddingClasses,
        {
          container: !disableInnerContainer,
        }
      )}>
        <p>No items available</p>
      </div>
    )
  }

  return (
    <>
      {/* FAQ Schema JSON-LD for SEO */}
      {faqSchema && (
        <script
          id={`faq-schema-${faqItems[0]?.id || 'default'}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLdStringify(faqSchema) }}
        />
      )}

      <div className={cn(
        "box-border flex flex-col gap-[42px] lg:gap-[72px] items-start justify-start relative size-full",
        paddingClasses,
        {
          container: !disableInnerContainer,
        }
      )}>
        {/* Expanding Items */}
        <div className="flex flex-col w-full max-w-[1296px]">
          {/* Top Divider */}
          <div className="h-px w-full border-t border-[#d1ccbe]" />

          {processedItems.map((item) => (
            <React.Fragment key={item.id}>
              <ExpandingItemComponent
                item={item}
                isExpanded={expandedItem === item.id}
                onToggle={() => toggleItem(item.id)}
              />
              {/* Divider between items */}
              <div className="h-px w-full border-t border-[#d1ccbe]" />
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  )
}